import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, forkJoin, from, map, of, switchMap } from 'rxjs';
import { downloadBlob } from 'src-ironside/helpers/file';
import { RestfulService } from 'src-ironside/services/restful.service';
import { TypeRequest } from 'src/app/shared/enums/type-request';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { DynamicViewInfoService } from 'src/app/shared/services/dynamic-view-info.service';
import { Filter } from './common.repository';
import { ResourceRepository } from './resource.repository';
import { TariffRepository } from './tariff.repository';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src-ironside/services/common.service';

export type Product = {
    weight?: number;
    description?: string;
    longDescription?: string;
    shortDescription?: any;
    brand?: string | null;
    createdAt?: number;
    updatedAt?: number;
    id?: string;
    image?: string;
    shortContent?: string;
    title?: string;
    packaging?: string;
    standard?: string;
    referenceProduct?: string;
    specification?: string;
    tariffs?: string[];
    resources?: any[];
    logisticVariables?: any[];
    isFavorite?: boolean;
    linkedProducts?: any[];
    referenceParentProduct?: string;
    gencodeProduct?: string;
    state?: DynamicValueMultiLanguage;
    other?: LanguageProperty | string;
    fullExtendData?: ProductExtendInfo[];
    partners?: string[];
    cutomsCode?: number;
    countryOfManufacture?: string;
    linkToParentProduct?: Array<{ documentId: string; type: string; value: string }>;
    brandOrigin?: { parameterId?: string };
    packagingOrigin?: { parameterId: string };
    linkToRessources?: any[];
    categoryCode?: any;
    new?: boolean;
    newStartDate?: number;
    newEndDate?: number;
    categoryOrigin?: any;
    department?: any;
    departmentOrigin?: any;
    linkToPartnerReference?: any[];
    orderNumber?: number;
    categoryPath?: any;
    widthProduct?: LanguageProperty;
    heightProduct?: LanguageProperty;
    lengthProduct?: LanguageProperty;
    termsOfPayment?: LanguageProperty;
    afterSalesService?: LanguageProperty;
    minimumOrderValue?: LanguageProperty;
    supplyQuota?: LanguageProperty;
    deliveryTimes?: LanguageProperty;
    lastPriceUpdate?: string;
    statusProduct?: DynamicValueMultiLanguage;
    resourceParent?: any[];
    associatedProduct?: any;
    linkToTheReplacementArticle?: any;
    manufacturerReference?: string;
    ce?: boolean;
    otherStandards?: LanguageProperty;
    battery?: LanguageProperty;
    batteryWeight?: number;
};

export interface DynamicValueMultiLanguage {
    labels: LanguageProperty;
    parameterId: string;
    value: string;
}

export type Logistic = {
    gencodeProduct?: string;
    grossWeight: number;
    height: number;
    width: number;
    depth: number;
    weight: number;
    typeOfVl: string;
    conditioning: number;
};

export type ProductExtendInfo = {
    des?: string;
    packs: string[];
    partners: any[];
    prixs?: any[];
    refEan: string;
    spec?: string;
    other?: LanguageProperty;
    gencodeProduct?: string;
    files?: string;
    url?: any[];
    logistics?: Logistic[];
    cutomsCode?: number;
    countryOfManufacture?: string;
    id?: string;
    state?: any;
    new?: boolean;
    newStartDate?: number;
    newEndDate?: number;
    typeOfResource?: any;
    packaging?: any;
    resources?: any[];
    packSmallest?: any;
    widthProduct?: LanguageProperty;
    heightProduct?: LanguageProperty;
    lengthProduct?: LanguageProperty;
    associatedProduct?: any;
    linkToTheReplacementArticle?: any;
    manufacturerReference?: string;
    weight?: number;
    battery?: LanguageProperty;
    batteryWeight?: number;
};

export interface LanguageProperty {
    da_DK: string;
    de_DE: string;
    en_GB: string;
    es_ES: string;
    fi_FI: string;
    fr_FR: string;
    it_IT: string;
    nl_NL: string;
    nn_NO: string;
    sv_SE: string;
}

export interface filterParams {
    fieldName: string;
    conditions: Array<{
        type: 'EQUALS' | 'IN' | 'CONTAINS';
        value: string;
    }>;
}

export interface DynamicProductParam {
    collectionName?: string;
    filters?: any;
    subQuery?: {
        filters?: any[];
        fieldName: string;
        type: TypeRequest;
    };
    fields?: string[];
    sorts?: {};
    locales?: string[];
    page?: number;
    pageSize?: number;
    isHandlingSpecialFields?: boolean;
    isUseLabel?: boolean;
    hideFields?: string[];
}

const DYNAMIC_PRODUCT = 'dynamic_product';

export type ProductFilter = {
    fieldName:
        | 'linkToTariff.documentId'
        | 'linkToSupplier.documentId'
        | 'brand.parameterId'
        | 'department.parameterId'
        | 'packaging.parameterId'
        | 'state.parameterId'
        | 'new'
        | 'categoryCode.documentId'
        | 'namePartner.documentId'
        | 'countryOfManufacture.parameterId'
        | 'referenceProduct'
        | 'creationDate'
        | 'id'
        | 'longDescription'
        | 'shortDescription'
        | 'linkToPartnerReference.value.documentId'
        | 'childProduct.documentId'
        | 'linkToParentProduct.documentId'
        | 'gencodeProduct';

    operator?: 'OR' | 'AND';
    values: string[];
};

@Injectable({
    providedIn: 'root',
})
export class ProductRepository extends RestfulService {
    constructor(
        public override httpClient: HttpClient,
        public override router: Router,
        private dynamicDocumentService: DynamicDocumentService,
        private dynamicViewService: DynamicViewInfoService,
        private datePipe: DatePipe,
        private resourceRepository: ResourceRepository,
        private translate: TranslateService,
        private _commonService: CommonService
    ) {
        super(httpClient, router);
    }

    logisticPriority = [
        'UNIT_WITH_PACKAGING',
        'CARTON_1',
        'CARTON_2',
        'PALLET',
        'TRUCK',
        'CONTAINER20',
        'CONTAINER40',
        'CONTAINER40_HQ',
        ,
    ];

    defaultFilters: Filter[] = [
        {
            fieldName: 'statusProduct',
            conditions: [
                {
                    type: 'EQUALS',
                    value: 'VALIDE',
                },
            ],
        },
        {
            fieldName: 'typeOfProduct',
            conditions: [
                {
                    type: 'EQUALS',
                    value: 'PERE',
                },
            ],
        },
    ];

    find(
        productFilters: ProductFilter[] = [],
        page = 1,
        pageSize = 150,
        direction: 'ASC' | 'DESC' = 'DESC'
    ): Observable<{
        content: any;
        meta: { totalPages: number; pageSize: number; totalItems: number };
    }> {
        const filterProducts: Filter[] = [...this.defaultFilters];
        productFilters.forEach((filter: ProductFilter) => {
            if (
                [
                    'referenceProduct',
                    'id',
                    'brand',
                    'longDescription.fr_FR',
                    'shortDescription.fr_FR',
                ].includes(filter.fieldName)
            ) {
                filterProducts.push({
                    fieldName: filter.fieldName,
                    conditions: filter.values.map((value) => ({ type: 'CONTAINS', value: value })),
                });
            } else {
                filterProducts.push({
                    fieldName: filter.fieldName,
                    conditions: [
                        {
                            type:
                                Array.isArray(filter.values) && filter.values.length > 1
                                    ? 'IN'
                                    : 'EQUALS',
                            value:
                                Array.isArray(filter.values) && filter.values.length === 1
                                    ? filter.values[0]
                                    : filter.values,
                        },
                    ],
                });
            }
        });

        return this.doPost(`dynamic-documents/${DYNAMIC_PRODUCT}/search`, {
            collectionName: DYNAMIC_PRODUCT,
            filters: filterProducts,
            page: page,
            pageSize: pageSize,
            sorts: {
                publishedOrder: direction,
            },
        }).pipe(
            map((res) => {
                return {
                    content: res?.content?.map((product: any) => this.convertProduct(product)),
                    meta: {
                        pageSize: res?.size,
                        totalPages: res?.totalPages,
                        totalItems: res?.totalElements,
                    },
                };
            })
        );
    }

    autocompleteFind(
        productFilters: any,
        page = 1,
        pageSize = 150,
        direction: 'ASC' | 'DESC' = 'DESC'
    ): Observable<{
        content: any;
        meta: { totalPages: number; pageSize: number; totalItems: number };
    }> {
        return this.doPost(`dynamic-documents/${DYNAMIC_PRODUCT}/user-search`, {
            collectionName: DYNAMIC_PRODUCT,
            filters: productFilters,
            page: page,
            pageSize: pageSize,
            sorts: {},
            locales: ['fr_FR'],
        }).pipe(
            map((res) => {
                return {
                    content: res?.content?.map((product: any) => this.convertProduct(product)),
                    meta: {
                        pageSize: res?.size,
                        totalPages: res?.totalPages,
                        totalItems: res?.totalElements,
                    },
                };
            })
        );
    }

    findById(id: string): Observable<Product> {
        return this.doGet(`dynamic-documents/dynamic_product/${id}`).pipe(
            map((res) => this.convertProduct(res))
        );
    }

    findDetailById(id: string): Observable<Product> {
        return this.doGet(`dynamic-documents/dynamic-product/${id}`).pipe(
            map((res) => this.convertProduct(res))
        );
    }

    findByIds(
        ids: string[],
        showLoading = true
    ): Observable<{
        content: any;
        meta: { totalPages: number; pageSize: number; totalItems: number };
    }> {
        return this.doPost(
            `dynamic-documents/dynamic_product/ids${showLoading ? '' : '?no-show-loading'}`,
            ids
        ).pipe(
            map((res) => {
                return {
                    content: res
                        ?.filter((product: any) => product.statusProduct?.value === 'VALIDE')
                        ?.map((product: any) => this.convertProduct(product)),
                    meta: {
                        pageSize: res?.size,
                        totalPages: res?.totalPages,
                        totalItems: ids.length,
                    },
                };
            })
        );
    }

    async loadFullExtendData(
        product: Product,
        isLoadPartner = true,
        isLoadResource = false
    ): Promise<ProductExtendInfo[]> {
        return new Promise(async (resolve) => {
            const result: ProductExtendInfo[] = [];
            let urls: [];

            if (!product.linkedProducts) return resolve(result);

            const linkedProducts = await firstValueFrom(
                this.findByIds(product.linkedProducts, false)
            );

            linkedProducts.content.forEach(async (p: any) => {
                let logisticVariables: any = [];
                if (p.logisticVariables) {
                    logisticVariables = await firstValueFrom(
                        this.dynamicDocumentService.findByIds(
                            'dynamic_logistic_variable',
                            p.logisticVariables,
                            true,
                            false
                        )
                    );
                }

                let partners: any = [];
                if (isLoadPartner) {
                    if (p.partners) {
                        partners = await firstValueFrom(
                            this.dynamicDocumentService.findByIds(
                                'dynamic_legal_entity',
                                p.partners,
                                true,
                                false
                            )
                        );
                    }
                }

                let tariffs: any = [];
                if (p.tariffs) {
                    tariffs = await firstValueFrom(
                        this.dynamicDocumentService.findByIds(
                            'dynamic_tariff',
                            p.tariffs,
                            true,
                            false
                        )
                    );
                }

                let resources: any = [];
                if (isLoadResource) {
                    if (p.resources) {
                        resources = await firstValueFrom(
                            this.dynamicDocumentService.findByIds(
                                'dynamic_ressource',
                                p.resources,
                                true,
                                false
                            )
                        );

                        urls = resources;
                        resources = resources?.filter(
                            (r: any) => r?.typeOfResource?.value == 'TI_ARTW' || 'PHOTO'
                        );
                    }
                }
                result.push({
                    refEan: `${p.referenceProduct || '_'} \n ${p.gencodeProduct || '_'}`,
                    des: p.shortDescription || '',
                    spec: p.specification || '',
                    prixs:
                        tariffs.map((t: any) => [
                            `${t.price} ${TariffRepository.mapCurrencyText(t.currency)}`,
                            `${
                                t.applicationStartDate
                                    ? this.datePipe.transform(t.applicationStartDate, 'dd-MM-yyyy')
                                    : '_'
                            } / ${
                                t.applicationEndDate
                                    ? this.datePipe.transform(t.applicationEndDate, 'dd-MM-yyyy')
                                    : '_'
                            } \n` +
                                `${t.incoterm?.labels?.fr_FR}${
                                    t.placeIncoterm ? ' / ' + t.placeIncoterm : ''
                                }`,
                            t.status.value,
                        ]) || [],
                    packs: logisticVariables.map((l: any) => l.typeOfVl?.labels?.fr_FR) || [],
                    partners: partners.map((p: any) => p.companyName),
                    other: typeof p.other === 'string' ? undefined : p.other,
                    gencodeProduct: p.gencodeProduct,
                    url: urls,
                    files: resources
                        .map((r: any) => r?.title)
                        .reduce(
                            (accumulator: string, currentValue: string, currentIndex: number) => {
                                return currentIndex < resources.length - 1
                                    ? accumulator + currentValue + ', '
                                    : accumulator + currentValue;
                            },
                            ''
                        ),
                    logistics: logisticVariables.map(
                        (item: any) =>
                            ({
                                typeOfVl: item?.typeOfVl?.labels?.fr_FR,
                                depth: item?.depth,
                                height: item?.height,
                                weight: item?.weight,
                                width: item?.width,
                                conditioning: item?.conditioning,
                            } as Logistic)
                    ),
                    cutomsCode: p.cutomsCode,
                    countryOfManufacture: p.countryOfManufacture,
                    state: p?.state,
                    new: p?.new,
                    newEndDate: p?.newEndDate,
                    newStartDate: p?.newStartDate,
                    packaging: p?.packaging,
                });
            });

            resolve(result);
        });
    }

    loadFullExtendDataByGroup(
        products: Product[],
        isWithdrawnFromSale?: boolean,
        payload?: DynamicProductParam
    ) {
        const productGroups = this.groupProducts(products, payload?.pageSize ?? 20);
        let listProduct: any[] = [];

        const groupObservables = productGroups.map((group) => {
            const idsParentItem = group.flatMap((product) => product.id || []);
            const filterMore = {
                fieldName: 'linkToParentProduct.documentId',
                conditions: idsParentItem?.map((value) => ({
                    type: 'EQUALS',
                    value: value,
                })),
            };
            if (payload) {
                payload = { ...payload, filters: [...payload?.filters, filterMore] };
            }

            const documentIds = group.flatMap((product) => product.linkedProducts || []);
            const finalPayload: DynamicProductParam = {
                ...payload,
                page: 1,
                pageSize: documentIds?.length || 100,
            };

            const observer = !payload
                ? this.findByIds(documentIds, false)
                : forkJoin([
                      this.findByIds(documentIds, false),
                      this.dynamicDocumentService.getParentProductByChildrent(finalPayload),
                  ]).pipe(
                      map(([findByIdsResult, dynamicResult]) => {
                          // Only extract images from findByIds, keep everything else from dynamicResult
                          const imagesMap = new Map();
                          findByIdsResult.content.forEach((item: any) => {
                              if (item.id && item.resources) {
                                  imagesMap.set(item.id, item.resources);
                              }
                          });

                          // Merge images into dynamicResult content
                          const mergedContent = dynamicResult.content.map((item: any) => {
                              return {
                                  ...item,
                                  resources: imagesMap.get(item.id) || item.resources,
                              };
                          });

                          return {
                              content: mergedContent,
                              meta: dynamicResult.meta,
                          };
                      })
                  );

            return from(observer).pipe(
                switchMap((linkedProductsResponse) => {
                    let linkedProducts;
                    /* if (!isWithdrawnFromSale) {
                        linkedProducts = linkedProductsResponse.content.filter(
                            (el: any) => el?.state?.value !== 'DISCONTINUED'
                        );
                    } else {
                        linkedProducts = linkedProductsResponse.content.filter(
                            (el: any) => el?.state?.value === 'DISCONTINUED'
                        );
                    } */

                    if (isWithdrawnFromSale) {
                        linkedProducts = linkedProductsResponse.content.filter(
                            (el: any) => el?.state?.value === 'DISCONTINUED'
                        );
                    } else {
                        linkedProducts = linkedProductsResponse.content;
                    }

                    listProduct = [...listProduct, ...linkedProducts];
                    const { logisticIds, partnerIds, tariffIds, resourceIds } =
                        this.extractIds(linkedProducts);
                    return this.fetchDynamicData(logisticIds, partnerIds, tariffIds, resourceIds);
                })
            );
        });

        return forkJoin(groupObservables).pipe(
            map((groupResults) => {
                const combinedLogisticVariables = groupResults.flatMap(
                    (result) => result.logisticVariables
                );
                const combinedPartners = groupResults.flatMap((result) => result.partners);
                const combinedTariffs = groupResults.flatMap((result) => result.tariffs);
                const combinedResources = groupResults.flatMap((result) => result.resources);

                this.enrichProducts(
                    products,
                    listProduct,
                    combinedLogisticVariables,
                    combinedPartners,
                    combinedTariffs,
                    combinedResources,
                    payload
                );

                return products;
            })
        );
    }

    private enrichProducts(
        products: Product[],
        listProduct: ProductExtendInfo[],
        combinedLogisticVariables: any[],
        combinedPartners: any[],
        combinedTariffs: any[],
        combinedResources: any[],
        payload: any
    ) {
        products.forEach((product) => {
            if (!product.fullExtendData) {
                product.fullExtendData = [];
            }

            listProduct.forEach((item) => {
                if (product.linkedProducts?.includes(item?.id)) {
                    product?.fullExtendData?.push(item);
                }
            });

            if (product.fullExtendData) {
                product.fullExtendData = product.fullExtendData.map((fullItem: Product) => {
                    // Update logistics
                    if (fullItem.logisticVariables && combinedLogisticVariables) {
                        fullItem.logisticVariables = fullItem.logisticVariables
                            .map((logVarId: string) => {
                                const matchedItem = combinedLogisticVariables.find(
                                    (item2) => item2.id === logVarId
                                );
                                return matchedItem ? matchedItem : logVarId;
                            })
                            .filter((item) => item?.status?.value == 'VALIDE');
                    }

                    // Update tariffs
                    if (fullItem.tariffs && combinedTariffs) {
                        fullItem.tariffs = fullItem.tariffs.map((tariff: string) => {
                            const matchedItem = combinedTariffs.find((item) => item.id === tariff);
                            return matchedItem ? matchedItem : tariff;
                        });
                    }

                    // Update partners
                    if (fullItem.linkToPartnerReference && combinedPartners) {
                        fullItem.partners = fullItem?.linkToPartnerReference?.map(
                            (partnerId: string) => {
                                const matchedItem = combinedPartners.find(
                                    (item2) => item2.id === partnerId
                                );
                                return matchedItem ? matchedItem : partnerId;
                            }
                        );
                    }

                    if (fullItem.resources && combinedResources) {
                        fullItem.resources = fullItem.resources.map((resourceId: string) => {
                            const matchedItem = combinedResources.find(
                                (item2) => item2?.id == resourceId
                            );
                            if (matchedItem) {
                                return {
                                    ...matchedItem,
                                    referentProduct: fullItem.referenceProduct,
                                };
                            }
                        });
                    }

                    return {
                        refEan: fullItem.referenceProduct || '',
                        des: fullItem.shortDescription || '',
                        spec: fullItem.specification || '',
                        prixs: fullItem?.tariffs,
                        packs:
                            fullItem.logisticVariables
                                ?.map((l: any) => l?.labels)
                                .filter(
                                    (label: any) =>
                                        label !== undefined || label?.status === 'VALIDE'
                                ) || [],
                        packSmallest: this.getSmallestConditioning(
                            fullItem.logisticVariables
                                ?.map((l: any) => l)
                                .filter((label: any) => label?.typeOfVl !== undefined) || []
                        ),
                        partners: fullItem.partners?.map((p: any) => p.linkToPartner) || [],
                        other: typeof fullItem.other === 'string' ? undefined : fullItem.other,
                        gencodeProduct: fullItem.gencodeProduct,
                        url: [],
                        files: '',
                        logistics: this.getSortTypeOfVl(
                            fullItem.logisticVariables?.map((item: any) => ({
                                gencodeProduct: item.gencodeProduct,
                                typeOfVl: item?.typeOfVl?.labels,
                                typeOfVlCode: item?.typeOfVl?.value,
                                depth: item?.depth,
                                height: item?.height,
                                weight: item?.weight,
                                width: item?.width,
                                grossWeight: item?.grossWeight,
                                conditioning: item?.conditioning,
                            })) || []
                        ),
                        cutomsCode: fullItem.cutomsCode,
                        countryOfManufacture: fullItem.countryOfManufacture,
                        state: fullItem?.state?.value === 'VALIDATED' ? null : fullItem?.state,
                        newStartDate: fullItem?.newStartDate,
                        newEndDate: fullItem?.newEndDate,
                        new: fullItem?.new,
                        resources: fullItem?.resources,
                        orderNumber: fullItem?.orderNumber,
                        widthProduct: fullItem?.widthProduct,
                        lengthProduct: fullItem?.lengthProduct,
                        heightProduct: fullItem?.heightProduct,
                        packaging: fullItem?.packaging,
                        id: fullItem?.id,
                        associatedProduct: fullItem?.associatedProduct ?? null,
                        linkToTheReplacementArticle: fullItem?.linkToTheReplacementArticle ?? null,
                        manufacturerReference: fullItem?.manufacturerReference,
                        weight: fullItem?.weight,
                        battery: fullItem?.battery,
                        batteryWeight: fullItem?.batteryWeight,
                    };
                });
                /*  if (!payload) { */
                product.fullExtendData = product.fullExtendData.sort((a: any, b: any) => {
                    if (a.orderNumber != null && b.orderNumber != null) {
                        return a.orderNumber - b.orderNumber;
                    }
                    if (a.orderNumber != null && b.orderNumber == null) {
                        return -1;
                    }
                    if (a.orderNumber == null && b.orderNumber != null) {
                        return 1;
                    }
                    const currentLang =
                        environment?.languagesExtranet?.find(
                            (lang: any) => lang.code === this.translate.currentLang
                        )?.locale || 'en_GB';
                    if (a.orderNumber == null && b.orderNumber == null) {
                        const titleA = (a.shortDescription?.[currentLang] || '').toLowerCase();
                        const titleB = (b.shortDescription?.[currentLang] || '').toLowerCase();
                        return titleA.localeCompare(titleB);
                    }
                    return 0;
                });
                /* } */
            }
        }); /* .sort((a: any, b: any) => {
                        // If both have orderNumber, sort by orderNumber
                        if (a.orderNumber != null && b.orderNumber != null) {
                            return a.orderNumber - b.orderNumber;
                        }

                        // If only a has orderNumber, a comes first
                        if (a.orderNumber != null && b.orderNumber == null) {
                            return -1;
                        }

                        // If only b has orderNumber, b comes first
                        if (a.orderNumber == null && b.orderNumber != null) {
                            return 1;
                        }

                        // If both are null, sort alphabetically by title
                        const currentLang =
                            environment?.languagesExtranet?.find(
                                (lang: any) => lang.code === this.translate.currentLang
                            )?.locale || 'en_GB';
                        if (a.orderNumber == null && b.orderNumber == null) {
                            const titleA = (a.shortDescription?.[currentLang] || '').toLowerCase();
                            const titleB = (b.shortDescription?.[currentLang] || '').toLowerCase();
                            return titleA.localeCompare(titleB);
                        }

                        return 0;
                    });; */
    }

    getSmallestConditioning(logisticVars: any[]) {
        if (!Array.isArray(logisticVars)) return null;
        return logisticVars
            .slice()
            .sort(
                (a, b) =>
                    this.logisticPriority.indexOf(a?.typeOfVlCode) -
                    this.logisticPriority.indexOf(b?.typeOfVlCode)
            )[0];
    }

    getSortTypeOfVl(logisticVars: any[]) {
        return logisticVars?.sort(
            (a, b) =>
                this.logisticPriority.indexOf(a?.typeOfVlCode) -
                this.logisticPriority.indexOf(b?.typeOfVlCode)
        );
    }

    fetchDynamicData(
        logisticIds: string[],
        partnerIds: string[],
        tariffIds: string[],
        resourceIds: string[]
    ) {
        return forkJoin([
            logisticIds.length
                ? this.dynamicDocumentService.findByIds(
                      'dynamic_logistic_variable',
                      logisticIds,
                      true,
                      false
                  )
                : of([]),
            partnerIds.length
                ? this.dynamicDocumentService.findByIds(
                      'dynamic_partner_reference',
                      partnerIds,
                      true,
                      false
                  )
                : of([]),
            tariffIds.length
                ? this.dynamicDocumentService.findByIds('dynamic_tariff', tariffIds, true, false)
                : of([]),

            resourceIds.length
                ? this.resourceRepository.getListImageResourceMultil(resourceIds)
                : of([]),
        ]).pipe(
            map(([logisticVariables, partners, tariffs, resources]) => {
                tariffs = tariffs.filter((el: any) => el.status.value === 'VALIDE');

                this._commonService.getSupplierQuantityStore().subscribe((res) => {
                    // Kiểm tra res có tồn tại và là array không
                    if (res && Array.isArray(res)) {
                        tariffs = tariffs.filter((el: any) => {
                            // Chỉ lọc nếu có linkToSupplier.documentId
                            if (!el.linkToSupplier?.documentId) {
                                return false;
                            }

                            // Tìm supplier tương ứng trong res
                            const supplierInfo = res.find(
                                (supplier: any) => supplier.id === el.linkToSupplier.documentId
                            );

                            // Chỉ giữ lại nếu supplier tồn tại và có status "PUBLIE"
                            return (
                                supplierInfo && supplierInfo.supplierStatusInfo?.valeur === 'PUBLIE'
                            );
                        });
                    }
                });
                resources = resources?.filter((el: any) => el?.status?.value == 'ACTIF');
                return { logisticVariables, partners, tariffs, resources };
            })
        );
    }

    private extractIds(linkedProducts: Product[]): {
        logisticIds: string[];
        partnerIds: string[];
        tariffIds: string[];
        resourceIds: string[];
    } {
        const logisticIds: string[] = [];
        const partnerIdsSet: Set<string> = new Set();
        const tariffIds: string[] = [];
        const resourceIds: string[] = [];

        linkedProducts.forEach((p: Product) => {
            p?.logisticVariables?.forEach((item: string) => {
                logisticIds.push(item);
            });
            p?.linkToPartnerReference?.forEach((item: string) => {
                partnerIdsSet.add(item);
            });
            p?.tariffs?.forEach((item: string) => {
                tariffIds.push(item);
            });
            p?.resources?.forEach((item: string) => {
                resourceIds.push(item);
            });
        });
        const partnerIds = Array.from(partnerIdsSet);
        return { logisticIds, partnerIds, tariffIds, resourceIds };
    }

    private groupProducts(products: Product[], groupSize: number): Product[][] {
        const productGroups: Product[][] = [];
        for (let i = 0; i < products.length; i += groupSize) {
            productGroups.push(products.slice(i, i + groupSize));
        }
        return productGroups;
    }

    findNewProduct(): Observable<Product[]> {
        const filters: Filter[] = [
            {
                fieldName: 'new',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: true,
                    },
                ],
            },
        ];

        return this.dynamicDocumentService
            .find(
                {
                    collectionName: DYNAMIC_PRODUCT,
                    filters: filters,
                    sorts: {
                        publishedOrder: 'DESC',
                    },
                    page: 1,
                    pageSize: 20,
                },
                true
            )
            .pipe(
                map((res) =>
                    res.content.map((product: any) => {
                        return this.convertProduct(product);
                    })
                )
            );
    }

    getViews() {
        return this.dynamicViewService.findByDocumentName(DYNAMIC_PRODUCT, false);
    }

    export(viewId: string, filters: ProductFilter[]) {
        this.dynamicDocumentService
            .exportFileSupplierOnView(DYNAMIC_PRODUCT, viewId, {
                ids: [],
                filters: [
                    ...filters.map((filter) => {
                        return {
                            conditions: [
                                {
                                    type:
                                        Array.isArray(filter.values) && filter.values.length > 1
                                            ? 'IN'
                                            : 'EQUALS',
                                    valeurMin: '',
                                    value:
                                        Array.isArray(filter.values) && filter.values.length === 1
                                            ? filter.values[0]
                                            : filter.values,
                                    valeurMax: '',
                                },
                            ],
                            fieldName: filter.fieldName,
                        };
                    }),
                    ...this.defaultFilters.map((filter: Filter) => {
                        return {
                            conditions: [
                                {
                                    type: 'EQUALS',
                                    valeurMin: '',
                                    value: filter.conditions[0].value,
                                    valeurMax: '',
                                },
                            ],
                            fieldName: filter.fieldName,
                        };
                    }),
                ],
                locales: ['fr_FR'],
                isUseLabel: 'true',
            })
            .subscribe((data: Blob) => {
                downloadBlob(data, 'xlsx');
            });
    }

    getMostViewedProduct(body: DynamicProductParam): Observable<Product[]> {
        return this.dynamicDocumentService
            .find(
                {
                    collectionName: DYNAMIC_PRODUCT,
                    filters: body.filters,
                    fields: body.fields,
                    sorts: {
                        hit: 'DESC',
                    },
                    page: 1,
                    pageSize: 20,
                    locales: ['fr_FR'],
                    timezone: 7,
                },
                true
            )
            .pipe(
                map((res) =>
                    res.content.map((product: any) => {
                        return this.convertProduct(product);
                    })
                )
            );
    }
    // --------
    convertProduct(data: any): Product {
        return {
            longDescription: data?.longDescription,
            shortDescription: data?.shortDescription,
            brand: data?.brand?.labels,
            packaging: data?.packaging?.[0]?.labels,
            createdAt: data?.creationDate,
            updatedAt: data?.updatingDate,
            standard: data?.standardNumberNf,
            referenceProduct: data?.referenceProduct,
            title: data?.subject ?? data?.shortDescription,
            description: data?.description,
            specification: data?.specification,
            resources: data?.linkToRessources?.map((resource: any) => resource.documentId),
            logisticVariables: data?.linkToLogisticVariable?.map(
                (logisticVariable: any) => logisticVariable.documentId
            ),
            tariffs: data?.linkToTariff?.map((tariff: any) => tariff.documentId),
            id: data?.id,
            linkedProducts: data?.linkToParentProduct?.map((product: any) => product.documentId),
            referenceParentProduct: data?.linkToParentProduct?.[0]?.value,
            gencodeProduct: data?.gencodeProduct,
            state: data?.state,
            other: data?.other,
            partners: data?.namePartner?.map((partner: any) => partner.documentId),
            cutomsCode: data?.cutomsCode,
            countryOfManufacture: data?.countryOfManufacture?.labels,
            brandOrigin: data?.brand,
            packagingOrigin: data?.packaging?.[0],
            categoryCode: data?.categoryCode?.[0]?.value || data?.categoryCode,
            new: data?.new,
            newStartDate: data?.newStartDate,
            newEndDate: data?.newEndDate,
            categoryOrigin: data?.categoryCode?.[0],
            department: data?.department?.[0]?.labels,
            linkToPartnerReference: data?.linkToPartnerReference?.map(
                (product: any) => product.documentId
            ),
            orderNumber: data?.orderNumber,
            widthProduct: data?.widthProduct,
            heightProduct: data?.heightProduct,
            lengthProduct: data?.lengthProduct,
            termsOfPayment: data?.termsOfPayment,
            afterSalesService: data?.afterSalesService,
            minimumOrderValue: data?.minimumOrderValue,
            supplyQuota: data?.supplyQuota,
            deliveryTimes: data?.deliveryTimes,
            lastPriceUpdate: data?.lastPriceUpdate,
            statusProduct: data?.statusProduct,
            associatedProduct: data?.associatedProduct,
            linkToTheReplacementArticle: data?.linkToTheReplacementArticle,
            manufacturerReference: data?.manufacturerReference,
            weight: data?.weight,
            ce: data?.ce,
            otherStandards: data?.otherStandards,
            battery: data?.battery,
            batteryWeight: data.batteryWeight,
        };
    }

    convertProductOriginalLanguage(data: any): Product {
        return {
            department: data?.department,
            longDescription: data?.longDescription,
            shortDescription: data?.shortDescription,
            brand: data?.brand?.labels,
            packaging: data?.packaging?.[0]?.labels,
            createdAt: data?.creationDate,
            updatedAt: data?.updatingDate,
            standard: data?.standardNumberNf,
            referenceProduct: data?.referenceProduct,
            title: data?.subject ?? data?.shortDescription,
            description: data?.description,
            specification: data?.specification,
            resources: data?.linkToRessources?.map((resource: any) => resource.documentId),
            logisticVariables: data?.linkToLogisticVariable?.map(
                (logisticVariable: any) => logisticVariable.documentId
            ),
            tariffs: data?.linkToTariff?.map((tariff: any) => tariff.documentId),
            id: data?.id,
            linkedProducts: data?.linkToParentProduct?.map((product: any) => product.documentId),
            gencodeProduct: data?.gencodeProduct,
            state: data?.state?.labels,
            other: data?.other,
            partners: data?.namePartner?.map((partner: any) => partner.documentId),
            cutomsCode: data?.cutomsCode,
            countryOfManufacture: data?.countryOfManufacture?.labels,
            brandOrigin: data?.brand,
            departmentOrigin: data?.department,
            packagingOrigin: data?.packaging?.[0],
            categoryCode: data?.categoryCode?.[0]?.value || data?.categoryCode,
            new: data?.new,
            newStartDate: data?.newStartDate,
            newEndDate: data?.newEndDate,
            orderNumber: data?.orderNumber,
            statusProduct: data?.statusProduct,
            ce: data?.ce,
            otherStandards: data?.otherStandards,
        };
    }
}
